import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/* One search box, many pages.

   The top bar owns the input; whichever page is on screen says what that input
   should filter by registering a scope. A page that registers takes over the
   box (placeholder included) and reads the typed term straight back, so the
   same state drives both the bar and the page's own inline search field. */

const noop = () => { };

export const PageSearchContext = createContext({
    query: '',
    setQuery: noop,
    placeholder: '',
    isSearchable: false,
    register: () => noop,
    provided: false,
});

let scopeCounter = 0;

export const PageSearchProvider = ({ children }) => {
    const [query, setQuery] = useState('');
    const [scopes, setScopes] = useState([]);
    const location = useLocation();
    const searchParams = location.search;

    /* A term typed for one page means nothing on the next, so every navigation
       starts clean - unless the new URL carries a ?q=, which is how the bar
       hands a term over when it sends you somewhere searchable. */
    useEffect(() => {
        const handover = new URLSearchParams(searchParams).get('q') || '';
        setQuery(handover);
        // only on a real page change; typing must not re-seed the box
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const register = useCallback((id, placeholder) => {
        setScopes((current) => [...current.filter((scope) => scope.id !== id), { id, placeholder }]);
        return () => setScopes((current) => current.filter((scope) => scope.id !== id));
    }, []);

    /* the most recently mounted scope wins, so a nested view can refine what
       its parent registered */
    const scope = scopes.length ? scopes[scopes.length - 1] : null;

    const value = useMemo(
        () => ({
            query,
            setQuery,
            placeholder: scope?.placeholder || '',
            isSearchable: Boolean(scope),
            register,
            provided: true,
        }),
        [query, scope, register]
    );

    return <PageSearchContext.Provider value={value}>{children}</PageSearchContext.Provider>;
};

/* Pages call this instead of their own useState: it claims the top bar's search
   box and returns the shared [term, setTerm] pair. */
export const usePageSearch = (placeholder = 'Search this page…') => {
    const shared = useContext(PageSearchContext);
    const { register } = shared;
    /* a page rendered on its own (a unit test, or outside the dashboard shell)
       keeps its own term rather than losing every keystroke */
    const [ownQuery, setOwnQuery] = useState('');
    const idRef = useRef(null);
    if (idRef.current === null) {
        scopeCounter += 1;
        idRef.current = `page-search-${scopeCounter}`;
    }

    useEffect(() => register(idRef.current, placeholder), [register, placeholder]);

    return shared.provided ? [shared.query, shared.setQuery] : [ownQuery, setOwnQuery];
};

export const useSearchBar = () => {
    const shared = useContext(PageSearchContext);
    const [ownQuery, setOwnQuery] = useState('');
    if (shared.provided) return shared;
    return { ...shared, query: ownQuery, setQuery: setOwnQuery };
};

export default PageSearchProvider;
